
import React, { useEffect, useState, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ComposedChart, Area, Line,
  PieChart, Pie, Cell,
} from "recharts";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  FaExchangeAlt, FaCheckCircle, FaTimesCircle, FaExclamationCircle,
  FaCalendarDay, FaStore, FaCreditCard, FaArrowUp, FaArrowDown, FaMinus,
} from "react-icons/fa";
import "../styles/Dashboard.css";
import {
  getReportingStats,
  getMerchantTerminalStats,
  getTotalTransactions,
  getMerchantStats,
  getMonthlyTransactionChart,
  getTransactionVolumeChart,
  getUnderperformingMerchants,
  getDashboardInsights,
  getTopPerformers,
  getMerchantTierDistribution,
} from "../api/api";

// ── Constants ────────────────────────────────────────────────────────────────
const RISK_COLORS = { Critical: "#ef4444", High: "#f97316", Medium: "#eab308" };
const TIER_COLORS = ["#4f8ef7", "#6e2e9c", "#f97316", "#94a3b8"];

// ── Custom bar chart tooltip ─────────────────────────────────────────────────
const BarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="tt-label">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color ?? p.fill }}>
          {p.name}: <strong>{Number(p.value).toLocaleString()}</strong>
        </p>
      ))}
    </div>
  );
};

// ── Custom volume chart tooltip ───────────────────────────────────────────────
const VolumeTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="tt-label">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.stroke || p.color }}>
          {p.name}: <strong>PKR {Number(p.value).toLocaleString()}</strong>
        </p>
      ))}
    </div>
  );
};

// ── Trend badge ───────────────────────────────────────────────────────────────
const TrendBadge = ({ value }) => {
  if (!value || value === 0)
    return <span className="trend neutral"><FaMinus /> 0%</span>;
  if (value > 0)
    return <span className="trend up"><FaArrowUp /> {value}%</span>;
  return <span className="trend down"><FaArrowDown /> {Math.abs(value)}%</span>;
};

// ── Risk badge ────────────────────────────────────────────────────────────────
const RiskBadge = ({ level }) => (
  <span className="risk-badge"
    style={{ background: RISK_COLORS[level] + "20", color: RISK_COLORS[level], borderColor: RISK_COLORS[level] + "50" }}>
    {level}
  </span>
);

// ── Custom legend for bar chart (matches target image) ────────────────────────
const BarLegend = () => (
  <div className="chart-legend">
    <span className="legend-item">
      <span className="legend-box blue" /> Transaction count
    </span>
    <span className="legend-item">
      <span className="legend-line red" /> Target (50K)
    </span>
  </div>
);

// ── Custom legend for volume chart ────────────────────────────────────────────
const VolumeLegend = () => (
  <div className="chart-legend">
    <span className="legend-item">
      <span className="legend-line green" /> Volume (M)
    </span>
    <span className="legend-item">
      <span className="legend-line-dashed amber" /> Avg deal size
    </span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
const Dashboard = ({ isSidebarOpen }) => {
  const [stats, setStats]                   = useState(null);
  const [merchantStats, setMerchantStats]   = useState(null);
  const [transactions, setTransactions]     = useState([]);
  const [monthlyData, setMonthlyData]       = useState([]);
  const [volumeData, setVolumeData]         = useState([]);
  const [underperforming, setUnderperforming] = useState([]);
  const [insights, setInsights]             = useState(null);
  const [topPerformers, setTopPerformers]   = useState([]);
  const [tierDistribution, setTierDistribution] = useState([]);
  const [loading, setLoading]               = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [
          reportingRes, merchantRes, txRes,
          merchantStatsRes, monthlyRes, volumeRes,
          underRes, insightsRes, topRes, tierRes,
        ] = await Promise.all([
          getReportingStats(),
          getMerchantTerminalStats(),
          getTotalTransactions(),
          getMerchantStats(),
          getMonthlyTransactionChart(),
          getTransactionVolumeChart(),
          getUnderperformingMerchants(),
          getDashboardInsights(),
          getTopPerformers(),
          getMerchantTierDistribution(),
        ]);

        if (reportingRes.isSuccess && merchantRes.isSuccess) {
          setStats({
            totalTransactions: reportingRes.summary.totalTransactions ?? 0,
            settledCount:      reportingRes.summary.settledCount      ?? 0,
            unsettledCount:    reportingRes.summary.unsettledCount    ?? 0,
            disputedCount:     reportingRes.summary.disputedCount     ?? 0,
            todaysTransactions: reportingRes.summary.todaysTransactions ?? 0,
            merchantCount:     merchantRes.stats.merchantCount         ?? 0,
            terminalCount:     merchantRes.stats.terminalCount         ?? 0,
          });
        }
        if (merchantStatsRes?.isSuccess) setMerchantStats(merchantStatsRes.data);
        if (txRes?.result?.data) {
          setTransactions(txRes.result.data.map(item =>
            Object.fromEntries(Object.entries(item).map(([k, v]) => [k, v || "--"]))
          ));
        }
        if (monthlyRes?.isSuccess)  setMonthlyData(monthlyRes.data);
        if (volumeRes?.isSuccess)   setVolumeData(volumeRes.data);
        if (underRes?.isSuccess)    setUnderperforming(underRes.data);
        if (insightsRes?.isSuccess) setInsights(insightsRes.data);
        if (topRes?.isSuccess)      setTopPerformers(topRes.data);
        if (tierRes?.isSuccess)     setTierDistribution(tierRes.data);
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const columns = useMemo(() => [
    { field: "ID",              header: "ID",          style: { width: "70px" } },
    { field: "MID",             header: "MID",         style: { width: "180px" } },
    { field: "TID",             header: "TID",         style: { width: "140px" } },
    { field: "PAN",             header: "PAN",         style: { width: "160px" } },
    { field: "Amount",          header: "Amount (PKR)", bodyClassName: "td-currency" },
    { field: "CardScheme",      header: "Card Scheme", style: { width: "130px" } },
    { field: "TransactionType", header: "Type",        style: { width: "110px" } },
    { field: "date",            header: "Date",        style: { width: "130px" } },
    { field: "time",            header: "Time",        style: { width: "110px" } },
    { field: "ResponseCode",    header: "Resp Code",   style: { width: "110px" } },
    { field: "STAN",            header: "Invoice No",  style: { width: "110px" } },
    { field: "RRN",             header: "RRN",         style: { width: "110px" } },
    { field: "AuthNumber",      header: "Auth Code",   style: { width: "110px" } },
  ], []);

  if (loading) {
    return (
      <div className={`db-container ${isSidebarOpen ? "shifted" : ""}`}>
        <div className="db-loader"><div className="loader-spinner" /><p>Loading dashboard…</p></div>
      </div>
    );
  }

  const maxTxn = topPerformers.length ? Math.max(...topPerformers.map(t => t.transactions)) : 1;

  // ── KPI top row (matches image 1: 5 cards) ──────────────────────────────
  const topKpis = merchantStats && stats ? [
    {
      label: "TOTAL MERCHANTS",
      value: merchantStats.total,
      sub: `+${insights?.newOnboardings ?? 0} this month`,
      subColor: "#3b82f6",
      accent: "#3b82f6",
    },
    {
      label: "ACTIVE MERCHANTS",
      value: merchantStats.active,
      sub: merchantStats.total ? `${((merchantStats.active / merchantStats.total) * 100).toFixed(1)}% of total` : "0%",
      subColor: "#22c55e",
      accent: "#22c55e",
    },
    {
      label: "INACTIVE MERCHANTS",
      value: merchantStats.inactive,
      sub: merchantStats.total ? `${((merchantStats.inactive / merchantStats.total) * 100).toFixed(1)}% of total` : "0%",
      subColor: "#ef4444",
      accent: "#ef4444",
    },
    {
      label: "TOTAL TRANSACTIONS",
      value: stats.totalTransactions,
      sub: "All time",
      subColor: "#8b5cf6",
      accent: "#8b5cf6",
    },
    {
      label: "TRANSACTIONS TODAY",
      value: stats.todaysTransactions,
      sub: insights?.todayTrend != null ? `↑ ${Math.abs(insights.todayTrend)}% vs yesterday` : "vs yesterday",
      subColor: "#f59e0b",
      accent: "#f59e0b",
      trendValue: insights?.todayTrend,
    },
  ] : [];

  return (
    <div className={`db-container ${isSidebarOpen ? "shifted" : ""}`}>

      {/* ── Top KPI Row ──────────────────────────────────────────────────── */}
      {topKpis.length > 0 && (
        <div className="top-kpi-row">
          {topKpis.map((k, i) => (
            <div key={i} className="top-kpi-card" style={{ "--accent": k.accent }}>
              <p className="top-kpi-label">{k.label}</p>
              <p className="top-kpi-value">{Number(k.value).toLocaleString()}</p>
              <p className="top-kpi-sub" style={{ color: k.subColor }}>{k.sub}</p>
              <div className="top-kpi-bar" style={{ background: k.accent }} />
            </div>
          ))}
        </div>
      )}

      {/* ── Transaction KPI tiles ────────────────────────────────────────── */}
      {stats && (
        <>
          <div className="db-section-title" style={{ marginTop: "2rem" }}>Transaction Overview</div>
          <div className="kpi-grid">
            {[
              { label: "Total Transactions",   value: stats.totalTransactions,   icon: <FaExchangeAlt />,     color: "total"     },
              { label: "Settled",              value: stats.settledCount,         icon: <FaCheckCircle />,     color: "settled"   },
              { label: "Unsettled",            value: stats.unsettledCount,       icon: <FaTimesCircle />,     color: "unsettled" },
              { label: "Disputed",             value: stats.disputedCount,        icon: <FaExclamationCircle />, color: "disputed"},
              { label: "Today's Transactions", value: stats.todaysTransactions,   icon: <FaCalendarDay />,     color: "today"     },
              { label: "Total Merchants",      value: stats.merchantCount,        icon: <FaStore />,           color: "merchants" },
              { label: "Total Terminals",      value: stats.terminalCount,        icon: <FaCreditCard />,      color: "terminals" },
            ].map((tile, i) => (
              <div key={i} className="kpi-card">
                <div className="kpi-icon">{tile.icon}</div>
                <div className="kpi-body">
                  <span className="kpi-label">{tile.label}</span>
                  <span className="kpi-value">{Number(tile.value).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Dual Charts — matches Image 1 exactly ────────────────────────── */}
      <div className="db-section-title" style={{ marginTop: "2rem" }}>Transaction Analytics</div>
      <div className="charts-row">

        {/* LEFT: Transactions per Month (blue bars + red dashed target) */}
        <div className="chart-card">
          <div className="chart-card-header">
            <span className="chart-title">Transactions per Month</span>
            <span className="chart-subtitle">3-month merchant performance overview</span>
          </div>
          <BarLegend />
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={monthlyData}
              margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
              barCategoryGap="35%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e8edf2" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 13, fill: "#6b7280", fontFamily: "Calibri" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#6b7280", fontFamily: "Calibri" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => v >= 1000 ? `${v / 1000}K` : v}
              />
              <Tooltip content={<BarTooltip />} cursor={{ fill: "#f1f5f9" }} />
              <ReferenceLine
                y={50000}
                stroke="#ef4444"
                strokeDasharray="6 4"
                strokeWidth={1.5}
              />
              <Bar
                dataKey="transactions"
                name="Transaction count"
                fill="#6c48c5"
                radius={[3, 3, 0, 0]}
                maxBarSize={72}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* RIGHT: Transaction Volume (green area + amber dashed line) */}
        <div className="chart-card">
          <div className="chart-card-header">
            <span className="chart-title">Transaction Volume (PKR)</span>
            <span className="chart-subtitle">Month-on-month monetary value</span>
          </div>
          <VolumeLegend />
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart
              data={volumeData}
              margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e8edf2" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 13, fill: "#6b7280", fontFamily: "Calibri" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 12, fill: "#6b7280", fontFamily: "Calibri" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => `PKR ${v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}`}
                width={80}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12, fill: "#6b7280", fontFamily: "Calibri" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => `PKR ${v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}`}
                width={70}
              />
              <Tooltip content={<VolumeTooltip />} />
              {/* Green area for volume */}
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="volume"
                name="Volume (M)"
                stroke="#6c48c5"
                strokeWidth={2.5}
                fill="#ede9ff"
                dot={{ r: 6, fill: "#22c55e", strokeWidth: 2, stroke: "#fff" }}
                activeDot={{ r: 8 }}
              />
              {/* Amber dashed line for avg deal size */}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="avgDealSize"
                name="Avg deal size"
                stroke="#f59e0b"
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={{ r: 5, fill: "#f59e0b", strokeWidth: 2, stroke: "#fff" }}
                activeDot={{ r: 7 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Underperforming Merchants ─────────────────────────────────────── */}
      {underperforming.length > 0 && (
        <>
          <div className="db-section-title" style={{ marginTop: "2rem" }}>
            Underperforming Merchants
            <span className="section-badge">≤15 transactions</span>
          </div>
          <div className="chart-card table-card">
            <table className="under-table">
              <thead>
                <tr>
                  <th>Merchant</th>
                  <th>MID</th>
                  <th>Transactions</th>
                  <th>Progress to Target</th>
                  <th>Risk Level</th>
                </tr>
              </thead>
              <tbody>
                {underperforming.map((m, i) => {
                  const pct = Math.min((m.transactions / 15) * 100, 100);
                  return (
                    <tr key={i}>
                      <td className="merchant-name-cell">{m.merchantName}</td>
                      <td className="mid-cell">{m.MID}</td>
                      <td className="txn-count">{m.transactions}</td>
                      <td className="progress-cell">
                        <div className="progress-bar-wrap">
                          <div className="progress-bar-fill" style={{
                            width: `${pct}%`,
                            background: pct < 33 ? "#ef4444" : pct < 66 ? "#f97316" : "#eab308",
                          }} />
                        </div>
                        <span className="progress-pct">{pct.toFixed(0)}%</span>
                      </td>
                      <td><RiskBadge level={m.riskLevel} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Quick Insights ────────────────────────────────────────────────── */}
      {insights && (
        <>
          <div className="db-section-title" style={{ marginTop: "2rem" }}>Quick Insights</div>
          <div className="insights-grid">
            {[
              { label: "Success Rate",          value: `${insights.successRate}%`,                                      icon: "✅", highlight: insights.successRate > 90 },
              { label: "Avg Transaction Value",  value: `PKR ${Number(insights.avgTransactionValue).toLocaleString()}`, icon: "💰" },
              { label: "Peak Hour",              value: insights.peakHour,                                               icon: "⏰" },
              { label: "Failed Transactions",    value: insights.failedTransactions,                                     icon: "❌", danger: true },
              { label: "New Onboardings",        value: insights.newOnboardings,                                         icon: "🆕" },
              { label: "Churn Risk",             value: insights.churnRisk,                                              icon: "⚠️", danger: insights.churnRisk > 5 },
            ].map((item, i) => (
              <div key={i} className={`insight-card ${item.danger ? "insight-danger" : ""} ${item.highlight ? "insight-success" : ""}`}>
                <span className="insight-icon">{item.icon}</span>
                <span className="insight-value">{item.value}</span>
                <span className="insight-label">{item.label}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Portfolio Health ──────────────────────────────────────────────── */}
      <div className="db-section-title" style={{ marginTop: "2rem" }}>Portfolio Health</div>
      <div className="charts-row">

        {/* Top 5 Performers */}
        <div className="chart-card">
          <div className="chart-card-header">
            <span className="chart-title">Top 5 Performers</span>
            <span className="chart-subtitle">by transaction count</span>
          </div>
          <div className="top-performers-list">
            {topPerformers.slice(0, 5).map((p, i) => (
              <div key={i} className="top-performer-row">
                <span className="rank-badge">#{i + 1}</span>
                <span className="performer-name">{p.merchantName}</span>
                <div className="performer-bar-wrap">
                  <div className="performer-bar" style={{
                    width: `${(p.transactions / maxTxn) * 100}%`,
                    background: "linear-gradient(90deg, #6c48c5, #1230AE)",
                    // background: "linear-gradient(90deg, #4f8ef7, #6e2e9c)",
                  }} />
                </div>
                <span className="performer-count">{p.transactions.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Donut */}
        <div className="chart-card donut-card">
          <div className="chart-card-header">
            <span className="chart-title">Merchant Tier Distribution</span>
            <span className="chart-subtitle">portfolio health segmentation</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={tierDistribution}
                cx="50%" cy="50%"
                innerRadius={60} outerRadius={95}
                paddingAngle={3}
                dataKey="value"
                label={({ name, percent }) => percent > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : ""}
                labelLine={false}
              >
                {tierDistribution.map((_, i) => (
                  <Cell key={i} fill={TIER_COLORS[i % TIER_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => [v, "Merchants"]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="donut-legend">
            {tierDistribution.map((t, i) => (
              <span key={i} className="donut-legend-item">
                <span className="donut-dot" style={{ background: TIER_COLORS[i % TIER_COLORS.length] }} />
                {t.name} ({t.value})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Recent Transactions Table ─────────────────────────────────────── */}
      

    </div>
  );
};

export default Dashboard;
